import { useCallback } from 'react';
import type { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import {
  useEndVideoCallMutation as useEndVideoCall,
  useTakePhotoMutation as useTakePhoto,
  Photo,
} from '@teachur/http-client';
import { VideoCall } from '../../../../types/VideoCall';
import { capturePhotoDataUrl } from '../lib/helpers';

interface UseVideoCallActionsProps {
  videoCall: VideoCall;
  closeVideoCall: () => void;
  setPhotos: (photos: Photo[] | ((prev: Photo[]) => Photo[])) => void;
  enterPiP: () => void;
}

export const useVideoCallActions = ({
  videoCall,
  closeVideoCall,
  setPhotos,
  enterPiP,
}: UseVideoCallActionsProps) => {
  const endMutation = useEndVideoCall();
  const takePhotoMutation = useTakePhoto();

  const onMinimize = useCallback(() => {
    enterPiP();
  }, [enterPiP]);

  const onHangUp = useCallback(() => {
    endMutation.mutate(videoCall.id.toString(), {
      onSuccess: () => closeVideoCall(),
      onError: (error: unknown) => {
        const axiosError = error as AxiosError;
        if (axiosError?.response?.status === 403) {
          closeVideoCall();
        }
      },
    });
  }, [endMutation, videoCall.id, closeVideoCall]);

  const onTakePhoto = useCallback(() => {
    const dataUrl = capturePhotoDataUrl('remote-video');
    takePhotoMutation.mutate(
      { videoCallId: videoCall.id.toString(), dataUrl },
      {
        onSuccess: (photo) => {
          setPhotos((prev) => [...prev, photo]);
          toast.success('Screenshot captured successfully!');
        },
        onError: () => {
          toast.error('Failed to capture screenshot');
        },
      }
    );
  }, [takePhotoMutation, videoCall.id, setPhotos]);

  return {
    onMinimize,
    onHangUp,
    onTakePhoto,
  };
};
